import * as React from 'react';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { Button, TextField, MenuItem } from '@mui/material';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { useSubdomain } from '../../context/SubdomainContext';

const steps = ['User Info', 'Organization', 'Choose Process'];

export default function LoginPopup({ onClose }) {
  const [open, setOpen] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    orgName: '',
    phoneNo: '',
    verticleId: '',
    noOfProducts: '',
    noOfServices: '',
  });
  const [verticleList, setVerticleList] = useState([]);
  const [phoneError, setPhoneError] = useState('');
  const [isFirstLogin, setIsFirstLogin] = useState(true);
  const navigate = useNavigate();
  const [skipped, setSkipped] = useState(new Set());
  const { subdomain } = useSubdomain();

  useEffect(() => {
    const getVerticles = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND}${subdomain}/tenantmodule/getverticles/`,
          {
            credentials: 'include',
          }
        );
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setVerticleList(data.map((item) => item));
      } catch (error) {
        console.error('Error fetching verticles:', error);
      }
    };

    const getData = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND}${subdomain}/tenantmodule/gettenantprofile/`,
          {
            credentials: 'include',
          }
        );
        if (res.status === 202) {
          const data = await res.json();
          setFormData((prevData) => ({
            ...prevData,
            name: data.first_name,
            role: data.role_name,
            orgName: data.org_name,
            verticleId: data.verticle,
            phoneNo: data.phone_number.toString(),
            noOfProducts: data.no_of_product,
            noOfServices: data.no_of_service,
          }));
          
        } else {
          console.log('Network response was not okay for details data fetch api');
        }
      } catch (error) {
        console.error('Error fetching tenant profile:', error);
      }
    };

    getData();
    getVerticles();
  }, [subdomain, navigate]);

  const handleClose = () => {
    setOpen(false);
    onClose(); // Call the onClose prop when closing the dialog
  };

  const handleNext = () => {
    if (activeStep === 1 && !formData.verticleId) {
      // If on the second step and verticleId is not filled, show an alert and prevent proceeding
      alert('Please select a vertical before proceeding.');
      return;
    }

    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }
    setActiveStep((prevStep) => prevStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSkip = () => {
    if (activeStep === 1 && !formData.verticleId) {
      alert('Please select a vertical before skipping.');
      return;
    }
  
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }
    
    if (activeStep === 2) {
      handleClose();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
      setSkipped((prevSkipped) => {
        const newSkipped = new Set(prevSkipped.values());
        newSkipped.add(activeStep);
        return newSkipped;
      });
    }
  };
  

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTextField = (event) => {
    const { name, value } = event.target;
    const modifiedValue = value.replace(/\d/g, '');
    setFormData({
      ...formData,
      [name]: modifiedValue,
    });
  };

  const handleContactField = (event) => {
    const { name, value } = event.target;
    const originalDigits = value.replace(/\D/g, '');
    if (originalDigits.length <= 10) {
      setFormData({
        ...formData,
        [name]: originalDigits,
      });
    }
    if (originalDigits.length !== 10) {
      setPhoneError('Phone number must be exactly 10 digits');
    } else {
      setPhoneError('');
    }
  };

  const isStepOptional = (step) => {
    return step === 0 || step === 1 || step === 2;
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const isFormFilled = () => {
    switch (activeStep) {
      case 0:
        return formData.name !== '' && formData.role !== '';
      case 1:
        return formData.orgName !== '' && formData.verticleId !== '' && formData.phoneNo.length === 10;
      case 2:
        return formData.verticleId !== '' && formData.noOfProducts !== '' && formData.noOfServices !== '';
      default:
        return false;
    }
  };

  const submitForm = async () => {
    if (!formData.verticleId) {
      alert('Please select a vertical before submitting.');
      return;
    }

    const request = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Referer: process.env.REACT_APP_HEADER,
        'X-CSRFToken': Cookies.get('csrftoken'),
      },
      credentials: 'include',
      body: JSON.stringify({
        first_name: formData.name,
        phone_number: parseInt(formData.phoneNo, 10),
        org_name: formData.orgName,
        verticle: parseInt(formData.verticleId, 10),
        no_of_product: parseInt(formData.noOfProducts, 10),
        no_of_service: parseInt(formData.noOfServices, 10),
      }),
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND}${subdomain}/tenantmodule/tenantprofile/`, request);
      if (res.status === 200 || res.status === 201) {
        handleClose(); // Call the onClose prop to close the popup
      } else {
        console.log('Network response was not ok');
      }
    } catch (err) {
      console.log('Error: ' + err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle></DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%' }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ marginBottom: '16px' }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {activeStep === 0 && (
              <Box>
                <TextField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Role"
                  name="role"
                  value={formData.role}
                  // onChange={handleTextField}
                  disabled
                  fullWidth
                  margin="normal"
                />
              </Box>
            )}
            {activeStep === 1 && (
              <Box>
                <TextField
                  label="Organization Name"
                  name="orgName"
                  value={formData.orgName}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Phone Number"
                  name="phoneNo"
                  value={formData.phoneNo}
                  onChange={handleContactField}
                  error={phoneError !== ''}
                  helperText={phoneError}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  select
                  label="Select Vertical"
                  name="verticleId"
                  value={formData.verticleId}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                >
                   {verticleList.map((verticle) => (
                    <MenuItem key={verticle.verticle_id} value={verticle.verticle_id}>
                      {verticle.verticle_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            {activeStep === 2 && (
              <Box>
                <TextField
                  label="Number of Products"
                  name="noOfProducts"
                  value={formData.noOfProducts}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Number of Services"
                  name="noOfServices"
                  value={formData.noOfServices}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {activeStep < steps.length - 1 && (
          <Button onClick={handleSkip}>Skip</Button>
        )}
        <Button onClick={handleBack} disabled={activeStep === 0}>
          Back
        </Button>
        <Button
          onClick={activeStep === steps.length - 1 ? submitForm : handleNext}
          disabled={!isFormFilled()}
        >
          {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
